import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Easing,
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';

import {getBrowserInfo, isMobileBrowser} from '/util/web/deviceDetection.util';
import {viewportManager} from '/util/web/viewport.util';

import {styles} from './MobileInterceptModal.style';

import {openNativeApp} from '/service/web/AppRedirection';

const MobileInterceptModal = () => {
  const {height: screenHeight} = useWindowDimensions();
  const [visible, setVisible] = useState(false);
  const [browserInfo, setBrowserInfo] = useState(() =>
    Platform.OS === 'web' ? getBrowserInfo() : {name: 'Browser', icon: ''},
  );
  const slideAnim = useRef(new Animated.Value(screenHeight)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isMobileBrowser() && !viewportManager.isDesktopModeForced()) {
      // Re-confirm info on mount
      setBrowserInfo(getBrowserInfo());
      setVisible(true);

      // Animate In
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
          easing: Easing.out(Easing.quad),
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
          easing: Easing.out(Easing.back(0.5)),
        }),
      ]).start();
    }
  }, []);

  const hideModal = (callback?: () => void) => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: screenHeight,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setVisible(false);
      if (callback) callback();
    });
  };

  const handleContinueOnWeb = () => {
    // hideModal(() => {
    //   viewportManager.enableDesktopMode();
    // });
    hideModal();
  };

  const handleOpenApp = () => {
    handleOpenAppAnalytics();
    openNativeApp();
  };

  const handleOpenAppAnalytics = () => {
    // Placeholder for analytics if needed
  };

  if (!visible) return null;

  return (
    <View style={styles.container} pointerEvents="box-none">
      {/* Backdrop */}
      <Animated.View
        style={[styles.backdrop, {opacity: fadeAnim}]}
        pointerEvents="auto">
        <Pressable
          style={styles.backdropPressable}
          accessibilityRole="button"
          accessibilityLabel="Modal backdrop"
        />
      </Animated.View>

      {/* Bottom Sheet */}
      <Animated.View
        style={[styles.bottomSheet, {transform: [{translateY: slideAnim}]}]}>
        <View style={styles.header}>
          <Text style={styles.title} accessibilityRole="header">
            Open Optavia Connect app
          </Text>
          <View style={styles.divider} />
        </View>

        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}>
          {/* Option 1: App */}
          <View style={styles.optionRow}>
            <View style={styles.leftContent}>
              <View style={styles.iconWrapper}>
                <Image
                  source={require('/asset/image/optavia_logo.png')}
                  style={styles.appIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.optionText}>Connect App</Text>
            </View>
            <TouchableOpacity
              style={styles.openButton}
              onPress={handleOpenApp}
              accessibilityLabel="Open in Connect App"
              accessibilityRole="button">
              <Text style={styles.openButtonText}>Open</Text>
            </TouchableOpacity>
          </View>

          {/* Option 2: Browser */}
          <View style={styles.optionRow}>
            <View style={styles.leftContent}>
              <View style={[styles.iconWrapper, styles.browserIconWrapper]}>
                <Image
                  source={{uri: browserInfo.icon}}
                  style={styles.browserIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.optionText}>{browserInfo.name}</Text>
            </View>
            <TouchableOpacity
              style={styles.continueButton}
              onPress={handleContinueOnWeb}
              accessibilityLabel={`Continue in ${browserInfo.name} browser`}
              accessibilityRole="button">
              <Text style={styles.continueButtonText}>Continue</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );
};

export default MobileInterceptModal;
